import type { ReactNode } from 'react';
import { useState, useCallback, useRef } from 'react';
import { StickyHeaderContext, type StickyHeaderContextType } from '@/contexts/StickyHeaderContext';

export function StickyHeaderProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [stickyHeader, setStickyHeaderState] = useState<ReactNode | null>(null);
  const registrationCount = useRef(0);
  const clearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setStickyHeader = useCallback((content: ReactNode | null): void => {
    if (clearTimeoutRef.current !== null) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
    setStickyHeaderState(content);
  }, []);

  const register = useCallback((): (() => void) => {
    registrationCount.current += 1;
    if (clearTimeoutRef.current !== null) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
    return (): void => {
      registrationCount.current -= 1;
      if (registrationCount.current === 0) {
        clearTimeoutRef.current = setTimeout((): void => {
          if (registrationCount.current === 0) setStickyHeaderState(null);
          clearTimeoutRef.current = null;
        }, 0);
      }
    };
  }, []);

  const value: StickyHeaderContextType = { stickyHeader, setStickyHeader, register };
  return <StickyHeaderContext.Provider value={value}>{children}</StickyHeaderContext.Provider>;
}
